import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { Car, Plus } from "shared/assets/icons";
import { Button, Card, Loader, Message, VehicleTeaser } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingUserDocuments } from "Bands/utils";

const vehicleCopy = copy.userVehicle;

export const Vehicles: FC = () => {
  const { vehicles, setShowCreateModal, setShowEditModal, setShowDeleteModal, getItemActions } =
    useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.vehicle,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.vehicle,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingVehicles } = useFetchingUserDocuments();

  const content = useMemo(() => {
    if (fetchingVehicles) {
      return <Loader />;
    }

    if (!vehicles?.length) {
      return <Message message={vehicleCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {vehicles?.map(vehicle => (
          <VehicleTeaser key={vehicle.id} {...vehicle} actions={getActions(vehicle.id)} />
        ))}
      </GridContainer>
    );
  }, [vehicles, fetchingVehicles, getActions]);

  return (
    <Card icon={Car} title={vehicleCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.vehicle)}
          data-testid="add_vehicle_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{vehicleCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
